import React, { useState, useEffect, memo } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import {
  Globe,
  MapPin,
  Users,
  Building2,
  User,
  RefreshCw,
  Loader2,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react';

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

// Memoize Geography to prevent unnecessary re-renders
const MemoizedGeography = memo(({ geo }) => (
  <Geography
    geography={geo}
    fill="#1E212C"
    stroke="#E8EDF5"
    strokeWidth={0.5}
    style={{
      default: { outline: 'none' },
      hover: { fill: '#F5F8FF', outline: 'none' },
      pressed: { fill: '#F5F8FF', outline: 'none' }
    }}
  />
));

MemoizedGeography.displayName = 'MemoizedGeography';

const GeographicDistribution = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [position, setPosition] = useState({ coordinates: [0, 20], zoom: 1 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getGeographicDistribution();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching geographic data:', error);
      toast.error('Failed to load geographic distribution');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleZoomIn = () => {
    if (position.zoom >= 4) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (position.zoom <= 1) return;
    setPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleReset = () => {
    setPosition({ coordinates: [0, 20], zoom: 1 });
  };

  const handleMoveEnd = (position) => {
    setPosition(position);
  };

  // Calculate marker size based on user count
  const getMarkerSize = (count) => {
    if (count >= 50) return 12;
    if (count >= 20) return 10;
    if (count >= 10) return 8;
    if (count >= 5) return 6;
    return 4;
  };

  // Get marker color based on dominant user type
  const getMarkerColor = (employers, employees) => {
    if (employers > employees) return '#1B5AFF'; // Coral for employers
    if (employees > employers) return '#5B8AF0'; // Blue for employees
    return '#9B59B6'; // Purple for mixed
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="geographic-distribution">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#5B8AF0]/20 flex items-center justify-center">
            <Globe className="w-5 h-5 text-[#5B8AF0]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#0A0F1E]">Geographic Distribution</h2>
            <p className="text-[#5B6472] text-sm">Transatlantic user liquidity - EU vs US</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#1E212C]"
            data-testid="refresh-geo-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1E212C] border border-[#E8EDF5] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#5B8AF0]/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-[#5B8AF0]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0F1E]">{data?.total_locations || 0}</p>
              <p className="text-[#5B6472] text-sm">Locations</p>
            </div>
          </div>
        </Card>
        <Card className="bg-[#1E212C] border border-[#E8EDF5] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#4CBA84]/20 flex items-center justify-center">
              <User className="w-5 h-5 text-[#4CBA84]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0F1E]">{data?.total_employees_with_location || 0}</p>
              <p className="text-[#5B6472] text-sm">Employees Located</p>
            </div>
          </div>
        </Card>
        <Card className="bg-[#1E212C] border border-[#E8EDF5] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1B5AFF]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#0A0F1E]">{data?.total_employers_with_location || 0}</p>
              <p className="text-[#5B6472] text-sm">Employers Located</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Map Card */}
      <Card className="bg-[#10131A] border border-[#E8EDF5] rounded-xl overflow-hidden relative" data-testid="world-map">
        {/* Map Controls */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          <Button
            onClick={handleZoomIn}
            variant="outline"
            size="icon"
            className="w-8 h-8 bg-[#1E212C] border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleZoomOut}
            variant="outline"
            size="icon"
            className="w-8 h-8 bg-[#1E212C] border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleReset}
            variant="outline"
            size="icon"
            className="w-8 h-8 bg-[#1E212C] border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* World Map */}
        <div className="relative h-[500px]">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 120,
              center: [0, 20]
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup
              zoom={position.zoom}
              center={position.coordinates}
              onMoveEnd={handleMoveEnd}
            >
              <Geographies geography={geoUrl}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <MemoizedGeography key={geo.rsmKey} geo={geo} />
                  ))
                }
              </Geographies>

              {/* Markers */}
              {data?.distribution?.map((location, index) => (
                <Marker
                  key={`${location.city}-${index}`}
                  coordinates={[location.lng, location.lat]}
                  onMouseEnter={() => setHoveredMarker(location)}
                  onMouseLeave={() => setHoveredMarker(null)}
                >
                  <circle
                    r={getMarkerSize(location.total_users)}
                    fill={getMarkerColor(location.employers, location.employees)}
                    fillOpacity={0.8}
                    stroke="#fff"
                    strokeWidth={1}
                    style={{ cursor: 'pointer' }}
                  />
                </Marker>
              ))}
            </ZoomableGroup>
          </ComposableMap>

          {/* Hover Tooltip */}
          {hoveredMarker && (
            <div 
              className="absolute bg-[#1E212C] border border-[#E8EDF5] rounded-lg p-3 shadow-xl z-20 pointer-events-none"
              style={{
                left: '50%',
                top: '20px',
                transform: 'translateX(-50%)'
              }}
            >
              <p className="text-[#0A0F1E] font-semibold mb-1">{hoveredMarker.city}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-[#5B8AF0]">
                  <User className="w-3 h-3 inline mr-1" />
                  {hoveredMarker.employees} employees
                </span>
                <span className="text-[#1B5AFF]">
                  <Building2 className="w-3 h-3 inline mr-1" />
                  {hoveredMarker.employers} employers
                </span>
              </div>
              <p className="text-[#5B6472] text-xs mt-1">
                Total: {hoveredMarker.total_users} users
              </p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="p-4 border-t border-[#E8EDF5] flex items-center justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#1B5AFF]" />
            <span className="text-[#5B6472] text-sm">Employer-heavy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#5B8AF0]" />
            <span className="text-[#5B6472] text-sm">Employee-heavy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#9B59B6]" />
            <span className="text-[#5B6472] text-sm">Mixed</span>
          </div>
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-[#E8EDF5]">
            <div className="w-2 h-2 rounded-full bg-white opacity-60" />
            <span className="text-[#5B6472] text-sm">Smaller = fewer users</span>
          </div>
        </div>
      </Card>

      {/* Top Locations Table */}
      <Card className="bg-[#1E212C] border border-[#E8EDF5] rounded-xl p-6" data-testid="top-locations">
        <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">Top Locations</h3>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {data?.distribution?.slice(0, 15).map((location, index) => (
            <div 
              key={`${location.city}-table-${index}`}
              className="flex items-center justify-between p-3 bg-[#10131A] rounded-lg hover:bg-[#1A1D26] transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-[#5B6472] text-sm w-6">{index + 1}.</span>
                <MapPin className="w-4 h-4 text-[#5B8AF0]" />
                <span className="text-[#0A0F1E]">{location.city}</span>
              </div>
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="border-[#5B8AF0] text-[#5B8AF0]">
                  <User className="w-3 h-3 mr-1" />
                  {location.employees}
                </Badge>
                <Badge variant="outline" className="border-[#1B5AFF] text-[#1B5AFF]">
                  <Building2 className="w-3 h-3 mr-1" />
                  {location.employers}
                </Badge>
                <span className="text-[#0A0F1E] font-medium w-16 text-right">
                  {location.total_users} total
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default GeographicDistribution;
